// Очередь офлайн-мутаций (IndexedDB). Разрешены только записи тренировки: PUT тренировки,
// PUT/DELETE подхода, POST /finish — они идемпотентны (UUID клиента), повтор не создаёт дублей.
//
// flush() шлёт по порядку, по одной:
//   успех            → убрать из очереди;
//   нет сети или 5xx → оставить, остановиться (порядок важен: подходы после тренировки);
//   401              → оставить всё, остановиться до входа (обработчик 401 уводит на /login);
//   423              → так же, до разблокировки приложения;
//   прочие 4xx       → вынести в «ошибки» с текстом сервера и идти дальше;
//   404 на DELETE    → подход уже удалён, считается доставленным.

import { useEffect, useState } from 'react'

import { ApiError, apiUrl, request } from '@/api/client'
import { captureIdentity, getActiveUserId, IDENTITY_HEADER, subscribeIdentity } from '@/api/identity'
import { useNetworkStatus } from '@/lib/online'

import { getOfflineDb, type OfflineDb, type FailedMutation, type MutationMethod, type QueuedMutation } from './db'

export type MutationInput = { method: MutationMethod; url: string; body?: unknown }

export type FlushResult = { sent: number; failed: number; stopped: null | 'network' | 'server' | 'auth' }

const UUID = '[0-9a-fA-F-]{36}'
const ALLOWED: { method: MutationMethod; pattern: RegExp }[] = [
  { method: 'PUT', pattern: new RegExp(`^/api/sessions/${UUID}$`) },
  { method: 'PUT', pattern: new RegExp(`^/api/sessions/${UUID}/sets/${UUID}$`) },
  { method: 'DELETE', pattern: new RegExp(`^/api/sessions/${UUID}/sets/${UUID}$`) },
  { method: 'POST', pattern: new RegExp(`^/api/sessions/${UUID}/finish$`) },
]

/** Можно ли эту запись делать без связи. */
export function isOfflineAllowed(method: string, url: string): boolean {
  const path = apiUrl(url)
  return ALLOWED.some((rule) => rule.method === method && rule.pattern.test(path))
}

// --- Подписка на изменения (счётчики в интерфейсе) ------------------------------------------

type Listener = () => void
const listeners = new Set<Listener>()

export function subscribeQueue(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function notify(): void {
  for (const listener of listeners) listener()
}

// --- Очередь ---------------------------------------------------------------------------------

/** id мутации, которая сейчас летит на сервер: в неё нельзя подливать новое тело. */
let sendingId: number | null = null
let authBlocked = false

/**
 * Поставить мутацию в очередь. Повторный PUT того же адреса, ещё не отправленный,
 * заменяет тело прежнего (лишние промежуточные состояния не шлются).
 */
export async function enqueue(mutation: MutationInput): Promise<number> {
  const url = apiUrl(mutation.url)
  if (!isOfflineAllowed(mutation.method, url)) {
    throw new Error(`Офлайн нельзя: ${mutation.method} ${url}`)
  }
  const owner = getActiveUserId()
  const offlineDb = getOfflineDb()
  if (!owner) throw new Error('Подтверди вход')
  const body = mutation.body ?? null
  const id = await offlineDb.transaction('rw', offlineDb.mutations, async () => {
    if (mutation.method === 'PUT') {
      const same = await offlineDb.mutations.where('url').equals(url).sortBy('id')
      const last = same[same.length - 1]
      if (last?.id !== undefined && last.method === 'PUT' && last.id !== sendingId) {
        await offlineDb.mutations.update(last.id, { body })
        return last.id
      }
    }
    return offlineDb.mutations.add({
      owner_id: owner,
      method: mutation.method,
      url,
      body,
      created_at: new Date().toISOString(),
      attempts: 0,
      last_error: null,
    })
  })
  notify()
  return id as number
}

export async function pendingMutations(): Promise<QueuedMutation[]> {
  if (!getActiveUserId()) return []
  return getOfflineDb().mutations.orderBy('id').toArray()
}

export async function failedMutations(): Promise<FailedMutation[]> {
  if (!getActiveUserId()) return []
  return getOfflineDb().failed.orderBy('id').toArray()
}

/** Убрать из очереди всё, что относится к адресу (и его подадресам); вернуть убранное. */
export async function dropMutations(urlPrefix: string): Promise<QueuedMutation[]> {
  const offlineDb = getOfflineDb()
  const prefix = apiUrl(urlPrefix)
  const dropped = await offlineDb.transaction('rw', offlineDb.mutations, async () => {
    const found = await offlineDb.mutations
      .filter((m) => m.url === prefix || m.url.startsWith(`${prefix}/`))
      .toArray()
    await offlineDb.mutations.bulkDelete(found.map((m) => m.id!))
    return found
  })
  notify()
  return dropped
}

/** Убрать запись из «ошибок» (человек прочитал). */
export async function dismissFailed(id: number): Promise<void> {
  await getOfflineDb().failed.delete(id)
  notify()
}

/** Очередь стоит, пока не войдёшь заново (последний flush получил 401). */
export function isAuthBlocked(): boolean {
  return authBlocked
}

async function sendOne(m: QueuedMutation, offlineDb: OfflineDb): Promise<'sent' | 'failed' | FlushResult['stopped']> {
  try {
    await request(m.method, m.url, m.method === 'DELETE' ? undefined : m.body, { headers: { [IDENTITY_HEADER]: m.owner_id } })
    return 'sent'
  } catch (error) {
    if (!(error instanceof ApiError)) {
      await offlineDb.mutations.update(m.id!, { attempts: m.attempts + 1, last_error: String(error) })
      return 'network'
    }
    if (error.status === 0 || error.status >= 500) {
      await offlineDb.mutations.update(m.id!, { attempts: m.attempts + 1, last_error: error.message })
      return error.status === 0 ? 'network' : 'server'
    }
    if (error.status === 401 || error.status === 423 || error.code === 'identity_changed') return 'auth'
    if (error.status === 404 && m.method === 'DELETE') return 'sent'
    await offlineDb.failed.add({
      owner_id: m.owner_id,
      method: m.method,
      url: m.url,
      body: m.body,
      created_at: m.created_at,
      failed_at: new Date().toISOString(),
      status: error.status,
      code: error.code,
      message: error.message,
    })
    return 'failed'
  }
}

async function runFlush(): Promise<FlushResult> {
  const result: FlushResult = { sent: 0, failed: 0, stopped: null }
  const identity = captureIdentity()
  if (!identity.id) return { ...result, stopped: 'auth' }
  const offlineDb = getOfflineDb()
  for (;;) {
    if (!identity.isCurrent()) { result.stopped = 'auth'; break }
    const next = await offlineDb.mutations.orderBy('id').first()
    if (!identity.isCurrent()) { result.stopped = 'auth'; break }
    if (!next || next.id === undefined) break
    if (next.owner_id !== identity.id) { result.stopped = 'auth'; break }
    sendingId = next.id
    const outcome = await sendOne(next, offlineDb)
    sendingId = null
    if (outcome === 'sent' || outcome === 'failed') {
      await offlineDb.mutations.delete(next.id)
      result[outcome === 'sent' ? 'sent' : 'failed'] += 1
      notify()
      continue
    }
    result.stopped = outcome
    break
  }
  if (identity.isCurrent()) authBlocked = result.stopped === 'auth'
  notify()
  return result
}

let running: Promise<FlushResult> | null = null
let again = false

/**
 * Отправить очередь. Одновременный вызов не запускает второй проход: он дожидается текущего,
 * а если за это время пришли новые мутации — делает ещё один.
 */
export function flush(): Promise<FlushResult> {
  const identity = captureIdentity()
  if (running) {
    again = true
    return running
  }
  running = (async () => {
    try {
      let total = await runFlush()
      while (again && total.stopped === null && identity.isCurrent()) {
        again = false
        const more = await runFlush()
        total = { sent: total.sent + more.sent, failed: total.failed + more.failed, stopped: more.stopped }
      }
      return total
    } finally {
      again = false
      running = null
    }
  })()
  return running
}

// --- Хуки -----------------------------------------------------------------------------------

/** Есть ли сеть (navigator.onLine). */
export function useOnline(): boolean {
  return useNetworkStatus()
}

function useQueueValue<T>(read: () => Promise<T>, initial: T): T {
  const [value, setValue] = useState<T>(initial)
  useEffect(() => {
    let alive = true
    let version = 0
    const refresh = () => {
      const current = ++version
      void read().then((v) => {
        if (alive && current === version) setValue(v)
      })
    }
    refresh()
    const unsubscribe = subscribeQueue(refresh)
    return () => {
      alive = false
      unsubscribe()
    }
  }, [read])
  return value
}

subscribeIdentity(() => { authBlocked = false; notify() })
const countPending = async () => getActiveUserId() ? getOfflineDb().mutations.count() : 0

/** Сколько изменений ещё не отправлено. */
export function usePendingCount(): number {
  return useQueueValue(countPending, 0)
}

/** Мутации, которые сервер отверг, — с текстом ошибки. */
export function useFailedMutations(): FailedMutation[] {
  return useQueueValue(failedMutations, [])
}
